'use client';

/**
 * ProductsSection — mini product grid for rail cards.
 *
 * Mode4-native parallel of `<ProductsSection />` under
 * copilot/details/sections/.
 *
 * Two-column thumbnail grid; items beyond `maxItems` truncate. Each
 * item is a small card with image (or placeholder) + name + price.
 * Click selects the product node so the rail can re-render.
 *
 * @layer presentation
 * @arch_component_type section
 * @arch_domain copilot / rail-insights
 */

import { Package } from 'lucide-react';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { cn } from '@/lib/utils/cn';
import { text } from '@/lib/design/tokens';

interface ProductLike {
  semantic_id?: string;
  name?: string;
  image_url?: string | null;
  list_price?: number | null;
}

interface ProductsSectionProps {
  products: ProductLike[] | null | undefined;
  label?: string | null;
  maxItems?: number | null;
}

export function ProductsSection({ products, label, maxItems }: ProductsSectionProps) {
  const selectNode = useGraphSelectionStore((s) => s.selectNode);  if (!products || products.length === 0) return null;

  const display = maxItems ? products.slice(0, maxItems) : products;
  const overflow = products.length - display.length;

  return (
    <div>
      <p className={text.eyebrow}>
        {label || 'Products'}{' '}
        <span className="text-muted-foreground">({products.length})</span>
      </p>
      <div className="grid grid-cols-2 gap-2">
        {display.map((p) => (
          <button
            key={p.semantic_id ?? p.name}
            onClick={() =>
              p.semantic_id &&
              selectNode({
                id: p.semantic_id,
                label: p.name ?? 'Product',
                type: 'product',
                properties: {
                  list_price: p.list_price,
                  image_url: p.image_url,
                },
              })
            }
            className="p-2 rounded-md bg-card border border-border hover:border-primary/60 transition-colors text-left"
          >
            <div className="aspect-square bg-muted rounded overflow-hidden mb-1 flex items-center justify-center">
              {p.image_url ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={p.image_url} alt={p.name ?? 'Product'} className="w-full h-full object-cover" />
              ) : (
                <Package className="h-6 w-6 text-muted-foreground/60" />
              )}
            </div>
            <div className={cn(text.title, 'truncate')}>{p.name ?? 'Product'}</div>
            {p.list_price != null && (
              <div className={text.muted}>${p.list_price.toFixed(0)}</div>
            )}
          </button>
        ))}
      </div>
      {overflow > 0 && (
        <p className={cn(text.muted, 'mt-1')}>+{overflow} more</p>
      )}
    </div>
  );
}
